/**
 * Catalogul evenimentelor de produs trimise la PostHog — singurul loc unde se scriu numele lor.
 *
 * Numele sunt `obiect_verb`, snake_case. Apelantii cheama functiile de aici, nu `capturePostHog`
 * direct: un nume scris gresit intr-un singur loc ar rupe tacut un funnel.
 *
 * 🔴 Date personale: nicio functie nu primeste email, nume, telefon, cod de profesor, clasa,
 * semnatura sau text liber. Parametrii sunt tipati pe valori inchise (rol, rezultat, numere,
 * id-uri interne); `error_fields` poarta NUMELE campurilor, niciodata valorile.
 * `schoolId` e deja pe persoana (identify), deci nu se repeta pe evenimente.
 */

import { capturePostHog } from './posthog';

export type RolInscriere = 'PROFESOR' | 'ELEV';
export type FluxCod = 'inregistrare' | 'autentificare';
export type TipEroare = 'server' | 'client';

function trimite(eveniment: string, proprietati?: Record<string, unknown>): void {
    if (!proprietati) {
        capturePostHog(eveniment);
        return;
    }
    const definite = Object.fromEntries(Object.entries(proprietati).filter(([, v]) => v !== undefined));
    capturePostHog(eveniment, definite);
}

export function trackSignupEmailChecked(outcome: 'verificare' | 'autentificare-elev' | 'fara-cont'): void {
    trimite('signup_email_checked', { outcome });
}

export function trackSignupEmailCheckFailed(httpStatus: number | undefined): void {
    trimite('signup_email_check_failed', { http_status: httpStatus });
}

export function trackSignupFormSubmitted(role: RolInscriere): void {
    trimite('signup_form_submitted', { role });
}

export function trackSignupFormFailed(props: {
    role: RolInscriere;
    errorType: TipEroare;
    httpStatus?: number;
    errorFields?: string[];
}): void {
    trimite('signup_form_failed', {
        role: props.role,
        error_type: props.errorType,
        http_status: props.httpStatus,
        error_fields: props.errorFields,
    });
}

export function trackOtpVerified(role: string | undefined, flow: FluxCod): void {
    trimite('otp_verified', { role, flow });
}

export function trackOtpVerifyFailed(flow: FluxCod, httpStatus: number | undefined): void {
    trimite('otp_verify_failed', { flow, http_status: httpStatus });
}

export function trackOtpResent(flow: FluxCod): void {
    trimite('otp_resent', { flow });
}

export function trackSchoolRequestSubmitted(props: {
    isResubmit: boolean;
    schoolStatus?: string;
    submittedAnyway: boolean;
    judet?: string;
}): void {
    trimite('school_request_submitted', {
        is_resubmit: props.isResubmit,
        school_status: props.schoolStatus,
        submitted_anyway: props.submittedAnyway,
        judet: props.judet || undefined,
    });
}

/**
 * `errorFields` = NUMELE campurilor respinse (ex. `adresaLivrare.codPostal`), niciodata valorile.
 * Pana pe 2026-09-28 pleca doar numarul, citit din `formState.errors` imediat dupa `trigger()` —
 * adica din instantaneul randarii anterioare, deci 13 din 20 de respingeri raportau „0 campuri".
 */
export function trackSchoolRequestFailed(errorType: TipEroare, errorFields: string[]): void {
    trimite('school_request_failed', {
        error_type: errorType,
        error_field_count: errorFields.length,
        error_fields: errorFields,
    });
}

export function trackSchoolConfirmationSubmitted(wantsToRegister: boolean): void {
    trimite('school_confirmation_submitted', { wants_to_register: wantsToRegister });
}

export function trackIntroVideoWatched(percent: number, skipped: boolean): void {
    trimite('intro_video_watched', { percent, skipped });
}

export function trackProtocolDownloaded(): void {
    trimite('protocol_downloaded');
}

export function trackTeacherApprovalSeen(): void {
    trimite('teacher_approval_seen');
}

/** Pleaca de pe persoana ADMIN-ului care decide; `teacherId` e id-ul intern al profesorului. */
export function trackSchoolRequestReviewed(props: {
    decision: 'approved' | 'rejected';
    teacherId?: string;
    isTakeover?: boolean;
}): void {
    trimite('school_request_reviewed', {
        decision: props.decision,
        teacher_id: props.teacherId,
        is_takeover: props.isTakeover,
    });
}

export function trackParentalConsentSubmitted(): void {
    trimite('parental_consent_submitted');
}

export function trackSignupButtonClicked(audience: 'elev', available: boolean): void {
    trimite('signup_button_clicked', { audience, available });
}

export function trackLessonStarted(props: {
    lessonType?: string;
    lessonId?: string;
    isPracticeMode: boolean;
    isResumed?: boolean;
}): void {
    trimite('lesson_started', {
        lesson_type: props.lessonType,
        lesson_id: props.lessonId,
        is_practice_mode: props.isPracticeMode,
        is_resumed: props.isResumed,
    });
}

export function trackLessonCompleted(props: {
    lessonType?: string;
    lessonId?: string;
    isPracticeMode: boolean;
    slideCount?: number;
}): void {
    trimite('lesson_completed', {
        lesson_type: props.lessonType,
        lesson_id: props.lessonId,
        is_practice_mode: props.isPracticeMode,
        slide_count: props.slideCount,
    });
}
